import type { AirlineItem } from "@seagames/contracts";
import { type ColumnDef, DataTable, ListHeader, ListToolbar, PrimaryCell, useUiStrings } from "@seagames/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { type AirlineFormTarget, AirlineFormModal } from "../../components/airlines/airline-form-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { Timestamp } from "../../components/common/list-columns";
import { ActiveBadge } from "../../components/sports/form-parts";
import { AirlineLogo } from "../../components/travel/airline-logo";
import { airlinesListOptions } from "../../lib/api/airlines";
import { requireAdminRole } from "../../lib/auth/guards";
import { regionName } from "../../lib/format/region";
import { listSearch } from "../../lib/list-page/list-search";
import { useListPage } from "../../lib/list-page/use-list-page";

/**
 * A BOOLEAN in the URL, not the "true"/"false" the API takes: TanStack
 * Router's default parser JSON-parses `?has_logo=true` into `true` (see
 * settings.countries.tsx). The string branch keeps a hand-typed URL working.
 */
const urlBool = z.union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")]);
const wire = (v: boolean | undefined): "true" | "false" | undefined => (v === undefined ? undefined : v ? "true" : "false");

export const Route = createFileRoute("/_app/settings/airlines")({
	beforeLoad: ({ context }) => requireAdminRole(context.session),
	// The API has one fixed order (active first, then name), so `sort` is never sent.
	validateSearch: listSearch(["name"], { has_logo: urlBool, is_active: urlBool }),
	component: AirlinesPage,
});

interface AirlineFilters extends Record<string, unknown> {
	search: string;
	hasLogo: "" | "true" | "false";
	isActive: "" | "true" | "false";
}
const EMPTY_FILTERS: AirlineFilters = { search: "", hasLogo: "", isActive: "" };

/**
 * Game Settings › Airlines (0087): the airlines flights are drawn with, and
 * their logos. ADMIN reads, SUPER_ADMIN writes (the API's rule; the page
 * offers writes on the active role, like its sibling pages). Offset pages,
 * the total in the same statement; search on code (IATA / ICAO) or name.
 */
function AirlinesPage() {
	const { t } = useTranslation("airlines");
	const { formatNumber } = useUiStrings();
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const [form, setForm] = useState<AirlineFormTarget | null>(null);

	const page = useListPage({
		search,
		navigate: Route.useNavigate(),
		listOptions: (q) => airlinesListOptions({ page: q.page, page_size: q.page_size, q: q.search, has_logo: wire(search.has_logo), is_active: wire(search.is_active) }),
		columnsKey: "airlines",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s): AirlineFilters => ({ search: s.search ?? "", hasLogo: wire(s.has_logo) ?? "", isActive: wire(s.is_active) ?? "" }),
			toUrl: (f) => ({
				search: f.search.trim() || undefined,
				has_logo: f.hasLogo === "" ? undefined : f.hasLogo === "true",
				is_active: f.isActive === "" ? undefined : f.isActive === "true",
			}),
		},
		summary: { range: (v) => t("pagination.summary", v), empty: t("pagination.empty") },
	});
	const total = page.list.data?.total;

	const columns: ColumnDef<AirlineItem, string>[] = [
		{
			key: "logo",
			header: t("table.logo"),
			width: "4.5rem",
			cell: (r) => <AirlineLogo code={r.iata_code} name={r.name} logoUrl={r.logo_url} />,
		},
		{
			key: "iata_code",
			header: t("table.iata"),
			width: "5rem",
			cell: (r) => <span className="font-mono text-[0.875rem] font-semibold tracking-[0.08em] text-gms-navy-ink">{r.iata_code}</span>,
		},
		{
			key: "icao_code",
			header: t("table.icao"),
			width: "5.5rem",
			cell: (r) => (r.icao_code ? <span className="font-mono text-[0.8125rem] tracking-[0.08em] text-gms-ink-2">{r.icao_code}</span> : <span className="text-gms-ink-3">—</span>),
		},
		{
			key: "name",
			header: t("table.name"),
			lockVisible: true,
			minWidth: "14rem",
			cell: (r) => <PrimaryCell>{r.name}</PrimaryCell>,
		},
		{
			key: "country_code",
			header: t("table.country"),
			minWidth: "9rem",
			cell: (r) =>
				r.country_code ? (
					<span className="text-gms-ink-2" title={r.country_code}>
						{regionName(r.country_code)}
					</span>
				) : (
					<span className="text-gms-ink-3">—</span>
				),
		},
		{
			key: "is_active",
			header: t("table.status"),
			minWidth: "6rem",
			cell: (r) => <ActiveBadge active={r.is_active} activeLabel={t("table.active")} inactiveLabel={t("table.inactive")} />,
		},
		{
			key: "logo_updated_at",
			header: t("table.logoUpdated"),
			minWidth: "9rem",
			cell: (r) => (r.logo_updated_at && r.logo_url ? <Timestamp iso={r.logo_updated_at} /> : <span className="text-gms-ink-3">{t("table.noLogo")}</span>),
		},
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("page.title")}
				count={total === undefined ? undefined : t("page.count", { count: total, n: formatNumber(total) })}
				description={t("page.intro")}
				actionLabel={t("page.add")}
				onAction={isSuper ? () => setForm({ mode: "create" }) : undefined}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("filters.searchPlaceholder")}
				chips={[
					{
						kind: "options",
						key: "logo",
						label: t("filters.logo"),
						options: [
							{ value: "true", label: t("filters.hasLogo") },
							{ value: "false", label: t("filters.noLogo") },
						],
						value: search.has_logo === undefined ? [] : [search.has_logo ? "true" : "false"],
						onChange: (v) => page.patch({ has_logo: v[0] === undefined ? undefined : v[0] === "true" }),
					},
					{
						kind: "options",
						key: "status",
						label: t("filters.status"),
						options: [
							{ value: "true", label: t("table.active") },
							{ value: "false", label: t("table.inactive") },
						],
						value: search.is_active === undefined ? [] : [search.is_active ? "true" : "false"],
						onChange: (v) => page.patch({ is_active: v[0] === undefined ? undefined : v[0] === "true" }),
					},
				]}
				onClearAll={page.clearFilters}
			/>

			<DataTable<AirlineItem, string>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.airline_id}
				empty={page.emptyState({
					filtered: { title: t("empty.filteredTitle"), description: t("empty.filteredDescription") },
					none: { title: t("empty.noneTitle"), description: t("empty.noneDescription") },
					clearLabel: t("empty.clearFilters"),
				})}
				rowActions={isSuper ? (r) => <EditDeleteActions editLabel={t("table.editLabel", { name: r.name })} onEdit={() => setForm({ mode: "edit", airline: r })} variant="list" /> : undefined}
			/>

			{isSuper && <AirlineFormModal target={form} onClose={() => setForm(null)} />}
		</div>
	);
}
